import { useState } from "react";
import { motion } from "framer-motion";
import { CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const empty = { full_name: "", email: "", address: "", phone: "" };

export const SignupDialog = ({ open, onOpenChange, isFull, onSuccess }) => {
  const [form, setForm] = useState(empty);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const set = (k) => (e) => {
    setForm((f) => ({ ...f, [k]: e.target.value }));
    setErrors((er) => ({ ...er, [k]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (form.full_name.trim().length < 2) e.full_name = "Enter your full name";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) e.email = "Enter a valid email";
    if (form.address.trim().length < 5) e.address = "Enter your address";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      const res = await api.post("/leads", {
        full_name: form.full_name.trim(),
        email: form.email.trim(),
        address: form.address.trim(),
        phone: form.phone.trim() || undefined,
      });
      setResult(res.data);
      onSuccess?.();
    } catch (err) {
      if (err.response?.status === 409) {
        setErrors({ email: "This email is already registered." });
      } else {
        toast.error("Something went wrong. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleOpenChange = (v) => {
    onOpenChange(v);
    if (!v) {
      setTimeout(() => {
        setForm(empty);
        setErrors({});
        setResult(null);
      }, 250);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent data-testid="signup-dialog" className="max-w-lg w-[calc(100%-2rem)] bg-[#0b0b0d] border-white/10 text-white">
        {result ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            className="py-6 text-center"
            data-testid="signup-confirmation"
          >
            <CheckCircle2 className="mx-auto h-14 w-14 text-volt" />
            <h3 className="mt-6 font-display uppercase text-2xl md:text-3xl tracking-tight" data-testid="confirmation-headline">
              {result.status === "founding" ? "Your founding spot is reserved" : "You're on the reserve list"}
            </h3>
            <p className="mt-3 text-white/70 text-sm leading-relaxed" data-testid="confirmation-detail">
              {result.status === "founding" ? (
                <>You're founding member <span className="text-volt font-bold">#{result.member_number} of 100</span>. Your $49/month rate is locked for life.</>
              ) : (
                <>The Founding 100 is full. You're first in line if a spot opens — we'll reach out by email.</>
              )}
            </p>
            <p className="mt-3 text-white/45 text-xs uppercase tracking-[0.2em]">
              No charge today — billing starts at opening, October 30, 2026
            </p>
            <button
              data-testid="confirmation-done-button"
              onClick={() => handleOpenChange(false)}
              className="mt-8 w-full bg-volt px-8 py-3.5 font-display uppercase tracking-wide text-white hover:bg-volt-dark transition-colors"
            >
              Done
            </button>
          </motion.div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-display uppercase text-2xl tracking-tight" data-testid="signup-dialog-title">
                {isFull ? "Join the reserve list" : "Reserve your founding spot"}
              </DialogTitle>
              <DialogDescription className="text-white/50 text-sm">
                {isFull
                  ? "The Founding 100 is full — leave your details and you're first in line."
                  : "$0 today — $49/month at opening. Founder rate locked for life."}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-5 mt-2" data-testid="signup-form">
              <div>
                <Label htmlFor="full_name" className="text-xs uppercase tracking-[0.2em] text-white/60">Full name *</Label>
                <Input
                  id="full_name"
                  data-testid="signup-name-input"
                  value={form.full_name}
                  onChange={set("full_name")}
                  placeholder="Jane Cooper"
                  className="mt-2 bg-white/5 border-white/10 text-white placeholder:text-white/25 focus-visible:ring-volt"
                />
                {errors.full_name && <p className="mt-1 text-xs text-red-400" data-testid="error-name">{errors.full_name}</p>}
              </div>
              <div>
                <Label htmlFor="email" className="text-xs uppercase tracking-[0.2em] text-white/60">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  data-testid="signup-email-input"
                  value={form.email}
                  onChange={set("email")}
                  placeholder="jane@email.com"
                  className="mt-2 bg-white/5 border-white/10 text-white placeholder:text-white/25 focus-visible:ring-volt"
                />
                {errors.email && <p className="mt-1 text-xs text-red-400" data-testid="error-email">{errors.email}</p>}
              </div>
              <div>
                <Label htmlFor="address" className="text-xs uppercase tracking-[0.2em] text-white/60">Address *</Label>
                <Input
                  id="address"
                  data-testid="signup-address-input"
                  value={form.address}
                  onChange={set("address")}
                  placeholder="Street, City, FL"
                  className="mt-2 bg-white/5 border-white/10 text-white placeholder:text-white/25 focus-visible:ring-volt"
                />
                {errors.address && <p className="mt-1 text-xs text-red-400" data-testid="error-address">{errors.address}</p>}
              </div>
              <div>
                <Label htmlFor="phone" className="text-xs uppercase tracking-[0.2em] text-white/60">Phone (optional)</Label>
                <Input
                  id="phone"
                  type="tel"
                  data-testid="signup-phone-input"
                  value={form.phone}
                  onChange={set("phone")}
                  placeholder="(305) 555-0123"
                  className="mt-2 bg-white/5 border-white/10 text-white placeholder:text-white/25 focus-visible:ring-volt"
                />
              </div>
              <button
                type="submit"
                disabled={submitting}
                data-testid="signup-submit-button"
                className="w-full inline-flex items-center justify-center gap-2 bg-volt px-8 py-4 font-display uppercase text-lg tracking-wide text-white hover:bg-volt-dark transition-colors disabled:opacity-60"
              >
                {submitting && <Loader2 className="h-5 w-5 animate-spin" />}
                {isFull ? "Join the reserve list" : "Reserve my spot — $0 today"}
              </button>
              <p className="text-center text-[11px] text-white/35 uppercase tracking-[0.2em]">No payment until opening · Cancel anytime</p>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
